import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useDispatch } from 'react-redux';
import { showMessage } from '~/actions';
import { logCustomEvent } from '~/api/analytics';
import { showErrorMessage } from '~/domain/utils';
import { apiMethods } from '~/services/api';
import { apiMethodsConst } from '~/services/methods';

export interface IPermanentJobData {
  start_date?: string;
  position?: string;
  rate?: string;
  benefits?: string;
  profession?: string;
  subProfession?: string | null;
  content?: string;
  workingInterview?: { interested: boolean; reason?: string[] };
  [key: string]: unknown;
}

type TPostPermanentJobError = AxiosError<{ message?: string; error?: string; title?: string }>;

/**
 * Legacy permanent-hire (PP) posting mutation. Formats the request form into the
 * text blob the endpoint expects, posts it, fires `permanent_post` analytics, and
 * shows the global success message (which routes home). Errors surface through the
 * shared error helper. No reducer bridge — this flow only ever drove `SHOW_MESSAGE`.
 */
export function usePostPermanentJobMutation() {
  const dispatch = useDispatch();

  return useMutation<unknown, TPostPermanentJobError, IPermanentJobData>({
    mutationFn: async (jobData) => {
      const text =
        `Start Date: ${jobData?.start_date}; Position: ${jobData?.position}; ` +
        `Rate: ${jobData?.rate}; Benefits: ${jobData?.benefits}; ` +
        `Profession: ${jobData?.profession}; SubProfession: ${jobData?.subProfession}; ` +
        `Content: ${jobData?.content};`;

      return await apiMethods[apiMethodsConst.PP_POSTING_REQUEST]({
        text,
        workingInterview: jobData?.workingInterview,
      });
    },
    onSuccess: (_response, jobData) => {
      void logCustomEvent('permanent_post', jobData);

      dispatch(
        showMessage({
          title: 'Request Successfully Submitted!',
          message: 'Your Permanent Hire request has been submitted.',
          route: '/',
          isSuccess: true,
          buttonText: 'Close',
        }),
      );
    },
    onError: (error) => {
      showErrorMessage({ error });
    },
  });
}
